
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { KeyRound, AlertTriangle, Loader2, Check, X, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSession } from '@/hooks/useSession';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";

const TwoFactorAuthSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [checking2FAStatus, setChecking2FAStatus] = useState(true);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const { toast } = useToast();
  const { session } = useSession();

  // Check if 2FA is already enabled when component mounts
  useEffect(() => {
    const check2FAStatus = async () => {
      if (!session) {
        setChecking2FAStatus(false);
        return;
      }

      try {
        const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (error) throw error;
        
        // If currentLevel is 'aal2', 2FA is enabled
        setIs2FAEnabled(data.currentLevel === 'aal2');
      } catch (error) {
        console.error("Error checking 2FA status:", error);
      } finally {
        setChecking2FAStatus(false);
      }
    };

    check2FAStatus();
  }, [session]);

  const startEnrollment = async () => {
    if (!session) {
      toast({
        title: "Authentication required",
        description: "You need to be logged in to manage two-factor authentication.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Start the enrollment process
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });
      
      if (error) throw error;
      
      setQrCodeUrl(data.totp.qr_code);
      setSecretKey(data.totp.secret);
      setShowQRDialog(true);
      
    } catch (error) {
      console.error("Error starting 2FA enrollment:", error);
      toast({
        title: "Something went wrong",
        description: "We couldn't start the 2FA enrollment process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndEnableMFA = async () => {
    if (!secretKey || !verificationCode || verificationCode.length < 6) {
      setVerifyError("Please enter a valid verification code");
      return;
    }

    try {
      setIsLoading(true);
      setVerifyError(null);
      
      const { data, error } = await supabase.auth.mfa.challenge({
        factorId: secretKey,
      });
      
      if (error) throw error;
      
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: secretKey,
        challengeId: data.id,
        code: verificationCode,
      });
      
      if (verifyError) throw verifyError;
      
      setIs2FAEnabled(true);
      setShowVerifyDialog(false);
      setShowQRDialog(false);
      
      toast({
        title: "2FA Enabled",
        description: "Two-factor authentication has been enabled for your account.",
      });
      
    } catch (error) {
      console.error("Error verifying 2FA:", error);
      setVerifyError("Invalid verification code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!session) {
      toast({
        title: "Authentication required",
        description: "You need to be logged in to manage two-factor authentication.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Get list of enrolled factors
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      
      if (factorsError) throw factorsError;
      
      // Find the TOTP factor
      const totpFactor = factorsData.totp.find(factor => factor.status === 'verified');
      
      if (!totpFactor) {
        throw new Error("No enrolled 2FA factor found");
      }
      
      // Unenroll the factor
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({
        factorId: totpFactor.id,
      });
      
      if (unenrollError) throw unenrollError;
      
      setIs2FAEnabled(false);
      
      toast({
        title: "2FA Disabled",
        description: "Two-factor authentication has been disabled for your account.",
      });
      
    } catch (error) {
      console.error("Error disabling 2FA:", error);
      toast({
        title: "Something went wrong",
        description: "We couldn't disable 2FA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copySecretKey = () => {
    if (secretKey) {
      navigator.clipboard.writeText(secretKey);
      toast({
        title: "Copied!",
        description: "Secret key copied to clipboard.",
      });
    }
  };

  const toggleTwoFactor = (value: boolean) => {
    if (value) {
      startEnrollment();
    } else {
      disableMFA();
    }
  };

  const handleVerificationComplete = (value: string) => {
    setVerificationCode(value);
    setVerifyError(null);
  };

  const proceedToVerification = () => {
    setShowQRDialog(false);
    setShowVerifyDialog(true);
  };

  return (
    <div className="border rounded-lg p-4 sm:p-6 bg-card w-full">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="p-2 rounded-full bg-primary/10 text-primary self-start">
          <KeyRound className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-medium">Two-Factor Authentication</h3>
          <p className="text-muted-foreground mt-1">
            Add an extra layer of security to your account by enabling two-factor authentication.
          </p>
          
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <Switch 
                id="2fa" 
                checked={is2FAEnabled}
                onCheckedChange={toggleTwoFactor}
                disabled={isLoading || checking2FAStatus}
              />
              <label htmlFor="2fa" className="text-sm font-medium">
                {checking2FAStatus ? 'Checking...' : is2FAEnabled ? "Enabled" : "Disabled"}
              </label>
            </div>
            
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> 
                Processing...
              </div>
            )}
          </div>
          
          {!session && (
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md flex items-start gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                You need to be logged in to enable two-factor authentication.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app or manually enter the secret key.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center space-y-4 py-4">
            {qrCodeUrl && (
              <div className="border rounded-md p-2 bg-white">
                <img src={qrCodeUrl} alt="QR Code for 2FA" className="w-64 h-64" />
              </div>
            )}
            
            {secretKey && (
              <div className="flex items-center w-full">
                <Label htmlFor="secret-key" className="sr-only">Secret Key</Label>
                <div className="relative w-full">
                  <Input 
                    id="secret-key" 
                    value={secretKey} 
                    readOnly 
                    className="pr-10 font-mono text-sm"
                  />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={copySecretKey}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setShowQRDialog(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={proceedToVerification}>
              Next
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verification Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Verify Authentication Code</DialogTitle>
            <DialogDescription>
              Enter the 6-digit code from your authenticator app to complete setup.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center space-y-4 py-4">
            <div className="w-full flex flex-col gap-2">
              <Label htmlFor="verification-code">Verification Code</Label>
              <InputOTP 
                maxLength={6} 
                value={verificationCode}
                onChange={setVerificationCode}
                onComplete={handleVerificationComplete}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              
              {verifyError && (
                <div className="text-red-500 text-sm mt-1">{verifyError}</div>
              )}
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowVerifyDialog(false);
                setShowQRDialog(true);
              }}
            >
              Back
            </Button>
            <Button 
              type="button" 
              onClick={verifyAndEnableMFA}
              disabled={isLoading || verificationCode.length < 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                  Verifying...
                </>
              ) : "Verify & Enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TwoFactorAuthSection;
